import { trackEvent } from 'utils/google-tag-manager';
import { useQuery } from '@tanstack/react-query';
import { WalkScoreErrorResponse, WalkScoreResponse } from 'pages/api/walk-score';
import { listingPageModalIds, testIds } from 'constants/test-constants';
import WalkingShoesIcon from 'components/icon/walking-shoes-icon';
import TransitBusIcon from 'components/icon/transit-bus-icon';
import BikeIcon from 'components/icon/bike-icon';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';
import { useState } from 'react';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { InView } from 'react-intersection-observer';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { 
  getWalkScoreDescriptionLabelKey, 
  getTransitScoreDescriptionLabelKey, 
  getBikeScoreDescriptionLabelKey, 
} from 'types/walk-score';

//Walk Score data is cached for 6 hours since it might not be updated frequently
const WALK_SCORE_STALE_TIME = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

export type WalkScoreProps = {
  latitude: number;
  longitude: number;
};

export default function WalkScore({ latitude, longitude }: WalkScoreProps) {
  const [inView, setInView] = useState(false);
  const { t } = useTranslation(Namespaces.COMMON);

  const { data: walkScoreData } = useQuery({
    queryKey: ['WalkScore', latitude, longitude],
    staleTime: WALK_SCORE_STALE_TIME,
    retry: false,
    enabled: inView,
    queryFn: async () => {
      try {
        const response = await fetchWithRetry(`/api/walk-score?latitude=${latitude}&longitude=${longitude}`, {}, undefined, { retries: 0 });
        const data = await response.json();

        if (data.error) {
          const walkScoreError = data as WalkScoreErrorResponse;
          trackEvent(`walk-score-error-${walkScoreError.errorCode}`, 'Walk Score', walkScoreError?.errorMessage);
          console.error(walkScoreError?.errorMessage);
          // Throw error to prevent caching
          throw new Error(walkScoreError?.errorMessage);
        } else {
          trackEvent('walk-score-impression', ' Walk Score');
          return data as WalkScoreResponse;
        }
      } catch (error) {
        console?.error('Failed to fetch walk score: ' + error);
        // Throw error to prevent caching
        throw error;
      }
    },
  });

  if (!latitude || !longitude) {
    return null;
  }

  return (
    <InView onChange={setInView}>
      {walkScoreData ? (
        <div className={styles.component} data-testid={listingPageModalIds.walkScoreComponent}>
          <ScoreChart
            score={walkScoreData?.walkScore}
            description={t(getWalkScoreDescriptionLabelKey(walkScoreData?.walkScoreDescription), { 
              defaultValue: walkScoreData?.walkScoreDescription, 
            })}
            walkScoreLink={walkScoreData?.walkScoreLink}
            Icon={WalkingShoesIcon}
            scoreType="Walk"
            t={t}
          />
          <ScoreChart
            score={walkScoreData?.transitScore}
            description={t(getTransitScoreDescriptionLabelKey(walkScoreData?.transitScoreDescription), { 
              defaultValue: walkScoreData?.transitScoreDescription, 
            })}
            walkScoreLink={walkScoreData?.walkScoreLink}
            Icon={TransitBusIcon}
            scoreType="Transit"
            t={t}
          />
          <ScoreChart
            score={walkScoreData?.bikeScore}
            description={t(getBikeScoreDescriptionLabelKey(walkScoreData?.bikeScoreDescription), { 
              defaultValue: walkScoreData?.bikeScoreDescription, 
            })}
            walkScoreLink={walkScoreData?.walkScoreLink}
            Icon={BikeIcon}
            scoreType="Bike"
            t={t}
          />
        </div>
      ) : (
        null
      )}
    </InView>
  );
}

type ScoreChartProps = {
  score?: number;
  description?: string;
  Icon: any;
  scoreType: 'Walk' | 'Bike' | 'Transit';
  walkScoreLink?: string;
  t: (key: string, options?: any) => string;
};

const walkScoreFallbackLink = 'https://www.walkscore.com/score';

const getScoreLabel = (score?: number) => !score ? '-' : score;

const getDescription = (description: string | undefined, t: (key: string, options?: any) => string) => 
  description || t(LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_NO_DATA, { defaultValue: 'No Data' });

const getCircleColour = (score?: number) => {
  if (!score) {
    return 'grey';
  } else if (score < 40) {
    return 'red';
  } else if (score >= 40 && score < 60) {
    return 'yellow';
  } else {
    return 'green';
  }
};

const ScoreChart = ({ score, description, Icon, scoreType, walkScoreLink = walkScoreFallbackLink, t }: ScoreChartProps) => {
  return (
    <section>
      <div className={styles.stat}>
        <Icon />
        <a href={walkScoreLink} target="_blank" rel="noopener noreferrer">
          <div className={buildClassName(styles.score, styles[getCircleColour(score)])} data-testid={scoreType + testIds.label}>
            {getScoreLabel(score)}
          </div>
        </a>
      </div>
      <div className={styles.description} data-testid={scoreType + testIds.description}>
        {getDescription(description, t)}
      </div>
      <sub>
        <a href={walkScoreLink} target="_blank" rel="noopener noreferrer">
          {scoreType} Score®
        </a>
      </sub>
    </section>
  );
};